import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import api from '../services/api';

/**
 * OpenCashBoxForm - Form to open a new cash box session
 * Allows entering initial cash breakdown by bill denomination
 */
const OpenCashBoxForm = ({ boxName, onSuccess, lastBaseLeft }) => {
    const [breakdown, setBreakdown] = useState({
        '100k': 0,
        '50k': 0,
        '20k': 0,
        '10k': 0,
        '5k': 0,
        '2k': 0,
        '1k': 0,
        'coins': 0
    });
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const billValues = {
        '100k': 100000,
        '50k': 50000,
        '20k': 20000,
        '10k': 10000,
        '5k': 5000,
        '2k': 2000,
        '1k': 1000,
        'coins': 1
    };

    const calculateTotal = () => {
        let total = 0;
        for (const [denom, qty] of Object.entries(breakdown)) {
            if (denom === 'coins') {
                total += parseFloat(qty || 0);
            } else {
                total += (billValues[denom] || 0) * parseInt(qty || 0);
            }
        }
        return total;
    };

    const handleBreakdownChange = (denomination, value) => {
        setBreakdown(prev => ({
            ...prev,
            [denomination]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const total = calculateTotal();

            if (total === 0) {
                setError('Debe ingresar al menos algún monto de base inicial');
                setLoading(false);
                return;
            }

            await api.post('/cash-box/open', {
                opening_balance: total,
                breakdown,
                box_name: boxName,
                notes
            });

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('Error opening cash box:', err);
            setError(err.response?.data?.error || 'Error al abrir caja');
        } finally {
            setLoading(false);
        }
    };

    const total = calculateTotal();

    return (
        <Card className="max-w-4xl mx-auto">
            <CardHeader>
                <CardTitle className="text-2xl">📂 Apertura de Caja</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                    Ingrese la base de cambio inicial del día
                </p>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit}>
                    {/* Bill breakdown inputs */}
                    <div className="bill-input-grid">
                        <div className="bill-input-field">
                            <label>Billetes de $100,000:</label>
                            <input
                                type="number"
                                min="0"
                                value={breakdown['100k']}
                                onChange={(e) => handleBreakdownChange('100k', e.target.value)}
                                placeholder="0"
                            />
                        </div>

                        <div className="bill-input-field">
                            <label>Billetes de $50,000:</label>
                            <input
                                type="number"
                                min="0"
                                value={breakdown['50k']}
                                onChange={(e) => handleBreakdownChange('50k', e.target.value)}
                                placeholder="0"
                            />
                        </div>

                        <div className="bill-input-field">
                            <label>Billetes de $20,000:</label>
                            <input
                                type="number"
                                min="0"
                                value={breakdown['20k']}
                                onChange={(e) => handleBreakdownChange('20k', e.target.value)}
                                placeholder="0"
                            />
                        </div>

                        <div className="bill-input-field">
                            <label>Billetes de $10,000:</label>
                            <input
                                type="number"
                                min="0"
                                value={breakdown['10k']}
                                onChange={(e) => handleBreakdownChange('10k', e.target.value)}
                                placeholder="0"
                            />
                        </div>

                        <div className="bill-input-field">
                            <label>Billetes de $5,000:</label>
                            <input
                                type="number"
                                min="0"
                                value={breakdown['5k']}
                                onChange={(e) => handleBreakdownChange('5k', e.target.value)}
                                placeholder="0"
                            />
                        </div>

                        <div className="bill-input-field">
                            <label>Billetes de $2,000:</label>
                            <input
                                type="number"
                                min="0"
                                value={breakdown['2k']}
                                onChange={(e) => handleBreakdownChange('2k', e.target.value)}
                                placeholder="0"
                            />
                        </div>

                        <div className="bill-input-field">
                            <label>Billetes de $1,000:</label>
                            <input
                                type="number"
                                min="0"
                                value={breakdown['1k']}
                                onChange={(e) => handleBreakdownChange('1k', e.target.value)}
                                placeholder="0"
                            />
                        </div>

                        <div className="bill-input-field">
                            <label>Monedas y otros:</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={breakdown['coins']}
                                onChange={(e) => handleBreakdownChange('coins', e.target.value)}
                                placeholder="$ 0"
                            />
                        </div>
                    </div>

                    {/* Total display */}
                    <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-300">
                        <div className="text-sm text-gray-700 mb-1">Base inicial:</div>
                        <div className="text-3xl font-bold text-green-700">
                            ${total.toLocaleString('es-CO')}
                        </div>
                    </div>

                    {/* Suggestion for Opening Balance */}
                    {lastBaseLeft > 0 && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                            <div className="text-sm text-blue-800">
                                💡 El cierre anterior dejó una base de <strong>${lastBaseLeft.toLocaleString('es-CO')}</strong>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    // Reset breakdown and put full amount in 'coins' for simplicity
                                    // unless we want to keep denominations. 
                                    // For now, setting it as 'coins' is the most direct way to match the amount.
                                    setBreakdown({
                                        '100k': 0, '50k': 0, '20k': 0, '10k': 0, '5k': 0, '2k': 0, '1k': 0,
                                        'coins': lastBaseLeft
                                    });
                                }}
                                className="text-xs font-bold uppercase py-1 px-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                            >
                                Usar esta base
                            </button>
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div className="alert-box alert-error mt-4">
                            {error}
                        </div>
                    )}

                    {/* Submit button */}
                    <button
                        type="submit"
                        disabled={loading || total === 0}
                        className="w-full mt-6 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? '⏳ Abriendo caja...' : '✅ Abrir Caja'}
                    </button>
                </form>
            </CardContent>
        </Card>
    );
};

export default OpenCashBoxForm;
